import {
  Box,
  Button,
  Divider,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  ModalBody,
  ModalCloseButton,
  ModalHeader,
  Select,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { CircleFlag } from 'react-circle-flags'
import { phoneNumbers } from '../../../utils/constants/PhoneDial'
import FileUpload from '../../../components/common/media/FileUpload'

import { useToast } from '@chakra-ui/react'
import { useAuth, useUpdateProfile, useUpdateAvatar } from '../../../hooks/useAuth'
import handleErrorToast from '../../../components/common/toast/handleErrorToast'
import handleSuccessToast from '../../../components/common/toast/handleSuccessToast'

import validator from 'validator'
import InvalidInput from '../../../components/common/forms/InvalidInput'

const ProfilePage = () => {
  const toast = useToast()
  const { user } = useAuth()
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false

  const [countryIndex, setCountryIndex] = useState(
    user?.dial_code ? phoneNumbers.findIndex((country) => country.dial_code === `+${user?.dial_code}`) : 151
  )
  const [firstName, setFirstName] = useState(user?.first_name ?? '')
  const [lastName, setLastName] = useState(user?.last_name ?? '')
  const [phoneNumber, setPhoneNumber] = useState(user?.phone?.substring(1 + (user?.dial_code ?? '').toString().length))

  //const [location, setLocation] = useState(user?.location ?? '')
  //const [city, setCity] = useState(user?.city ?? '')

  const location = user?.location ?? ''
  const city = user?.city ?? ''

  const [country, setCountry] = useState(user?.country ?? 'NL')

  const [file, setFile] = useState<File | null>(null)

  const [inputErrors, setInputErrors] = useState({
    firstName: false,
    lastName: false,
    phoneNumber: false,
    location: false,
    city: false,
    country: false,
  })

  const validateSubmit = () => {
    const errors = {
      firstName: firstName === '',
      lastName: lastName === '',
      phoneNumber: !validator.isMobilePhone(
        `${phoneNumbers[countryIndex].dial_code}${(phoneNumber ?? '').replace(/^0+/, '')}`
      ),
      location: false,
      city: false,
      country: country === '',
    }

    setInputErrors(errors)
    return Object.values(errors).every((error) => !error)
  }

  const updateProfile = useUpdateProfile()
  const updateAvatar = useUpdateAvatar()

  useEffect(() => {
    if (file != null) {
      updateAvatar.mutate(
        { avatar: file },
        {
          onSuccess: () => handleSuccessToast(toast, 'Success!', 'Update sent successfully'),
          onError: () => handleErrorToast(toast, 'Error!', 'An error has occurred'),
        }
      )
    }
  }, [file])

  return (
    <>
      <Box m={{ base: 0, md: '40px 20px 20px 20px' }} overflowY="auto">
        {!isMobile && (
          <ModalHeader>
            <Box display="flex">
              <Text fontWeight="510" textStyle="h5" mb="8px">
                Personal Information
              </Text>
            </Box>
            <Box display="flex" justifyContent="center">
              <Text fontWeight="400" textStyle="b-sm" color="neutral.500">
                Stay connected seamlessly by keeping your personal details up-to-date. Easily edit your information for a
                more personalized experience with us.
              </Text>
            </Box>
          </ModalHeader>
        )}
        {isMobile && (
          <Box mb="20px">
            <Text fontSize="20px" fontWeight="600" color="neutral.900" lineHeight="1.3">
              Personal Information
            </Text>
            <Text mt="4px" fontWeight="400" textStyle="b-sm" color="neutral.500">
              Keep your personal details up-to-date for a more personalized experience.
            </Text>
          </Box>
        )}
        {!isMobile && <ModalCloseButton />}
        <ModalBody position="relative" mt={{ base: 0, md: '16px' }} px={{ base: 0, md: '24px' }}>
          <FileUpload
            isAvatar={true}
            src={user?.avatar ?? ''}
            detail="PNG, JPG, or WEBP (max. 2 MB)"
            accept=".png,.jpg,.jpeg,.webp"
            setFile={(f) => {
              if (f instanceof FileList) {
                setFile(f[0])
              } else {
                setFile(f)
              }
            }}
          />
          <Flex mb="12px" gap={{ base: '12px', md: '12px' }} direction={{ base: 'column', md: 'row' }}>
            <Box width={{ base: '100%', md: '50%' }}>
              <Text textStyle="b-sm" fontWeight="400" mb="4px" color="neutral.900">
                First Name
              </Text>
              <Input
                p="9px 12px"
                height={{ base: '44px', md: '40px' }}
                placeholder="Jennifer"
                fontSize={{ base: '16px', md: '14px' }}
                fontWeight="400"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              ></Input>
              <InvalidInput isError={inputErrors.firstName} label="first name" />
            </Box>
            <Box width={{ base: '100%', md: '50%' }}>
              <Text textStyle="b-sm" fontWeight="400" mb="4px" color="neutral.900">
                Last Name
              </Text>
              <Input
                p="9px 12px"
                height={{ base: '44px', md: '40px' }}
                placeholder="Khan"
                fontSize={{ base: '16px', md: '14px' }}
                fontWeight="400"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              ></Input>
              <InvalidInput isError={inputErrors.lastName} label="last name" />
            </Box>
          </Flex>

          <Box width={{ base: '100%', md: '50%' }}>
            <Text textStyle="b-sm" fontWeight="400" mb="4px" color="neutral.900">
              Phone Number
            </Text>
            <InputGroup>
              <InputLeftElement left="4px" top="4px" width="100px" bgColor="neutral.10" borderRadius="8px" pl="40px">
                <CircleFlag countryCode={phoneNumbers[countryIndex].code.toLowerCase()} height="16" width="16" />
                <Text ml="10px" fontSize="14px">
                  {phoneNumbers[countryIndex].dial_code}
                </Text>
                <Select
                  borderWidth="0"
                  p="8px 9px 8px"
                  bgColor="neutral.10"
                  height="32px"
                  fontSize="14px"
                  opacity="0"
                  right="20px"
                  onChange={(e) => setCountryIndex(parseInt(e.target.value))}
                  value={countryIndex}
                >
                  {phoneNumbers.map((country, i: number) => {
                    return (
                      <option key={i} value={i}>
                        {country.name}
                      </option>
                    )
                  })}
                </Select>
              </InputLeftElement>
              <Input
                height={{ base: '44px', md: '40px' }}
                fontSize={{ base: '16px', md: '14px' }}
                width="100%"
                placeholder="6493756534"
                padding="19px 36px 19px 110px"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
            </InputGroup>
            <InvalidInput isError={inputErrors.phoneNumber} label="phone number" />
          </Box>

          <Divider m="16px 0px" />

          <Box w={{ base: '100%', md: '200px' }} mb="20px" pl="2px">
            <Text textStyle="b-sm" fontWeight="400" mb="4px" color="neutral.900">
              Country
            </Text>
            <Select
              height={{ base: '44px', md: '40px' }}
              fontSize={{ base: '16px', md: '14px' }}
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            >
              {phoneNumbers.map((country, i: number) => {
                return (
                  <option key={i} value={country.code}>
                    {country.name}
                  </option>
                )
              })}
            </Select>
            <InvalidInput isError={inputErrors.country} label="country" />
          </Box>
          <Button
            variant="heavy"
            height={{ base: '48px', md: '40px' }}
            w={{ base: '100%', md: 'auto' }}
            isLoading={updateProfile.isPending}
            onClick={() => {
              if (validateSubmit()) {
                updateProfile.mutate(
                  {
                    id: user?.id ?? '',
                    email: user?.email ?? '',
                    first_name: firstName,
                    last_name: lastName,
                    phone: `${phoneNumbers[countryIndex].dial_code} ${phoneNumber}`,
                    location: location,
                    city: city,
                    country: country,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                  },
                  {
                    onSuccess: () => handleSuccessToast(toast, 'Success!', 'Update sent successfully'),
                    onError: () => handleErrorToast(toast, 'Error!', 'An error has occurred'),
                  }
                )
              }
            }}
          >
            Save Updates
          </Button>
        </ModalBody>
      </Box>
    </>
  )
}

export default ProfilePage
